import { action, makeObservable, observable, reaction, runInAction } from "mobx";

import { io, Socket } from "socket.io-client";
import { createStoreLogger } from "../../helpers/loggers";
import {
  WebSocketMessageMap,
  WebSocketEventName,
  WebSocketEventListener,
  WebSocketEventHandlers,
  AppointmentWebSocketEvent,
} from "./types";
import type AuthStore from "../Auth/authStore";

export default class WebSocketStore {
  private basePath = process.env.REACT_APP_API_URL;
  private logger;
  public socket: Socket;
  private disposeTokenReaction: (() => void) | null = null;
  private isInitialized = false;

  // Subscription tracking registry for reconnect replay
  private subscriptions = new Map<string, () => void>();

  // Observable for connection state
  @observable public isConnected: boolean = false;
  @observable public connectionError: string | null = null;

  public constructor(private authStore: AuthStore) {
    makeObservable(this);
    this.logger = createStoreLogger(this.constructor.name);

    // Connect to the WebSocket server using the current in-memory token
    this.socket = io(this.basePath, {
      auth: {
        token: authStore.authToken,
      },
      transports: ["websocket", "polling"],
      autoConnect: false,
    });

    this.logger.info("WebSocket connection defined", this.socket);

    // React to token changes from AuthStore (single source of truth)
    this.disposeTokenReaction = reaction(
      () => this.authStore.authToken,
      (token) => {
        if (token) {
          this.updateAuthToken(token);
        } else if (!this.authStore.isBootstrapping && this.socket.connected) {
          // This case happens when the token is cleared (e.g., logout) while the socket is connected.
          this.logger.info("🔌🚪 Auth token cleared, disconnecting socket");
          this.socket.disconnect();
        }
      },
    );
  }

  /**
   * Sets the auth token on the socket instance.
   * Extracted for reuse across updateAuthToken and ensureConnected.
   */
  private setSocketAuth(token: string): void {
    this.socket.auth = { token };
  }

  /**
   * Ensures the socket is connected with valid auth.
   * Safety net for cases where bootstrap race condition missed connection.
   * Returns true if connected (or connecting), false if no token available.
   */
  private ensureConnected(): boolean {
    if (this.socket.connected || this.socket.active) {
      return true;
    }

    if (!this.authStore.authToken) {
      this.logger.warn("🔌⚠️ Cannot connect socket - no auth token available");
      return false;
    }

    this.logger.info("🔌⚠️ Socket not connected, connecting now...");
    this.setSocketAuth(this.authStore.authToken);
    this.socket.connect();
    return true;
  }

  /**
   * Updates the socket's auth token for future reconnections
   * and optionally reconnects if currently connected
   */
  public updateAuthToken = (token: string) => {
    this.logger.info("🔌🔑 Updating socket auth token");
    this.setSocketAuth(token);

    if (!this.isInitialized) {
      this.logger.info("🔌⏸️ Socket listeners not initialized yet, deferring connect");
      return;
    }

    // If not connected, connect with new token
    if (!this.socket.connected && !this.socket.active) {
      this.logger.info("🔌🔄 Reconnecting socket with new token");
      this.socket.connect();
    }
  };

  @action
  initializeWebSocket = async () => {
    if (this.isInitialized) {
      return;
    }

    this.isInitialized = true;
    this.logger.info("🔌🚀 WebSocket store initialized");

    // Listen for the connection confirmation
    this.socket.on("connect", () => {
      this.isConnected = true;
      this.connectionError = null;
      this.replaySubscriptions();
      this.logger.info(`🔌✅ Connected! My socket id is ${this.socket.id}`);
    });

    this.socket.on("disconnect", () => {
      runInAction(() => {
        this.isConnected = false;
      });
      this.logger.info("🔌❌ Disconnected from WebSocket server");
    });

    this.socket.on("connect_error", (err) => {
      runInAction(() => {
        this.isConnected = false;
        this.connectionError = err.message;
      });
      this.logger.error("🔌💥 Socket Connection Error:", err.message);
    });

    // Listen for auth_error from server when token is expired/invalid
    this.socket.on("auth_error", async (data: { message?: string; code?: string }) => {
      this.logger.warn("🔌🔐 Auth error received from server:", data);

      // Attempt to refresh the token
      try {
        const success = await this.authStore.refreshToken();
        if (success && this.authStore.authToken) {
          this.logger.info("🔌🔄 Token refreshed after auth_error, reconnecting...");
          // Force disconnect and reconnect with new token
          this.socket.disconnect();
          // this.socket.auth = { token: this.authStore.authToken };
          // this.socket.connect();
          this.updateAuthToken(this.authStore.authToken);
        } else {
          this.logger.error("🔌❌ Token refresh failed after auth_error");
          this.connectionError = "Authentication failed - please log in again";
        }
      } catch (error) {
        this.logger.error("🔌💥 Error refreshing token after auth_error:", error);
        this.connectionError = "Authentication failed - please log in again";
      }
    });

    // Add a catch-all listener to debug what events are coming through
    // this.socket.onAny((eventName, ...args) => {
    //   this.logger.info(`🔌📡 Received event: ${eventName}`, args);
    // });

    // // Add listener for any emitted events (outgoing)
    // this.socket.onAnyOutgoing((eventName, ...args) => {
    //   this.logger.info(`🔌📤 Sent event: ${eventName}`, args);
    // });

    if (this.authStore.authToken) {
      this.updateAuthToken(this.authStore.authToken);
    }
  };

  // Type-safe event listener registration
  public on<T extends WebSocketEventName>(event: T, listener: WebSocketEventListener<T>): void {
    this.socket.on(event as any, listener as any);
  }

  // Type-safe event listener removal
  public off<T extends WebSocketEventName>(event: T, listener?: WebSocketEventListener<T>): void {
    if (listener) {
      this.socket.off(event as any, listener as any);
    } else {
      this.socket.off(event as any);
    }
  }

  // Type-safe event emission
  public emit<T extends WebSocketEventName>(event: T, data: WebSocketMessageMap[T]): void {
    this.socket.emit(event as any, data);
  }

  /**
   * Track a subscription for replay on reconnect.
   * Stores a replay function that will be called when the socket reconnects.
   */
  private trackSubscription(key: string, replay: () => void): void {
    this.logger.info(`🔌📍 Tracking subscription: ${key}`);
    this.subscriptions.set(key, replay);
  }

  /**
   * Remove a subscription from the tracking registry.
   * Called when unsubscribing or cleaning up.
   */
  private untrackSubscription(key: string): void {
    this.logger.info(`🔌📍 Untracking subscription: ${key}`);
    this.subscriptions.delete(key);
  }

  /**
   * Replay all tracked subscriptions on reconnect.
   * Ensures the client is re-subscribed to all rooms after disconnect/reconnect.
   */
  private replaySubscriptions(): void {
    this.logger.info(`🔌🔄 Replaying ${this.subscriptions.size} subscriptions`, {
      keys: [...this.subscriptions.keys()],
    });
    this.subscriptions.forEach((replay) => {
      try {
        replay();
      } catch (error) {
        this.logger.error("🔌💥 Error replaying subscription:", error);
      }
    });
  }

  /**
   * Generic subscribe — emits subscription event and tracks for replay on reconnect.
   * Works for all subscription types (agent, workflow, etc).
   *
   * @param subscribeEvent - The subscribe event name
   * @param key - Unique key for tracking (e.g., "agent:threadId123")
   * @param payload - The event payload
   */
  public subscribe<T extends WebSocketEventName>(
    subscribeEvent: T,
    key: string,
    payload: WebSocketMessageMap[T],
  ): void {
    this.logger.info(`🔌📤 Subscribing to ${subscribeEvent}`, { key });

    // Safety net: ensure socket is connected before subscribing
    this.ensureConnected();

    this.emit(subscribeEvent, payload);
    this.trackSubscription(key, () => this.emit(subscribeEvent, payload));
  }

  /**
   * Generic unsubscribe — emits unsubscribe event and removes from tracking.
   * Works for all subscription types (agent, workflow, etc).
   *
   * @param unsubscribeEvent - The unsubscribe event name
   * @param key - Unique key for tracking (e.g., "agent:threadId123")
   * @param payload - The event payload
   */
  public unsubscribe<T extends WebSocketEventName>(
    unsubscribeEvent: T,
    key: string,
    payload: WebSocketMessageMap[T],
  ): void {
    this.logger.info(`🔌📥 Unsubscribing from ${unsubscribeEvent}`, { key });
    this.emit(unsubscribeEvent, payload);
    this.untrackSubscription(key);
  }

  // Specialized method for appointment events
  public onAppointmentEvent(handler: (event: AppointmentWebSocketEvent) => void): () => void {
    const appointmentHandler = (data: any) => {
      this.logger.info("🔌⚡ Appointment WebSocket event received:", data);

      // The actual data is nested in data.webSocket
      const webSocketData = data.webSocket || data;

      // Debug: Log the actual structure of the data
      this.logger.info("🔌🔍 Data structure check:", {
        hasWebSocket: !!data.webSocket,
        hasType: !!webSocketData.type,
        hasAction: !!webSocketData.action,
        typeValue: webSocketData.type,
        actionValue: webSocketData.action,
        dataKeys: Object.keys(data),
        webSocketKeys: data.webSocket ? Object.keys(data.webSocket) : "no webSocket",
        fullData: data,
      });

      if (webSocketData.type && webSocketData.action) {
        this.logger.info("🔌✅ Calling handler with valid type and action");
        handler({
          type: webSocketData.type,
          action: webSocketData.action,
          data: webSocketData.data,
          source: webSocketData.source,
          clientId: webSocketData.clientId,
        });
      } else {
        this.logger.warn("🔌❌ Data missing type or action - handler not called", {
          type: webSocketData.type,
          action: webSocketData.action,
          webSocketData: webSocketData,
          originalData: data,
        });
      }
    };

    this.logger.info("🔌🎯 Setting up appointment event listeners...");

    // The server emits exactly ONE calendar sync event: "appointmentUpdate"
    // (wrapping { type, action, data, source, clientId }). The legacy names
    // (appointmentCreate/Delete/Updated/...) were never emitted — removed.
    this.on("appointmentUpdate", appointmentHandler);

    this.logger.info("🔌✅ Appointment event listener registered");

    // Return cleanup function
    return () => {
      this.logger.info("🔌🧹 Cleaning up appointment event listeners");
      this.off("appointmentUpdate", appointmentHandler);
    };
  }

  // Debug method to see what listeners are registered
  public getRegisteredEvents(): string[] {
    // Socket.IO doesn't expose eventNames directly, but we can track our registered events
    this.logger.info("🔌📋 Checking registered events - socket connected:", this.isConnected);
    return ["appointmentUpdate", "connect", "disconnect", "connect_error", "auth_error"];
  }

  // Method to manually trigger a test event (for debugging)
  public sendTestMessage(): void {
    this.logger.info("🔌🧪 Sending test message to server");
    this.socket.emit("test", { message: "Hello from client", timestamp: new Date().toISOString() });
  }

  // Batch event handler registration
  public registerEventHandlers(handlers: WebSocketEventHandlers): () => void {
    const cleanupFunctions: (() => void)[] = [];

    Object.entries(handlers).forEach(([event, handler]) => {
      if (handler) {
        this.on(event as WebSocketEventName, handler as any);
        cleanupFunctions.push(() => this.off(event as WebSocketEventName, handler as any));
      }
    });

    // Return function to cleanup all handlers
    return () => {
      cleanupFunctions.forEach((cleanup) => cleanup());
    };
  }

  // Disconnect and cleanup
  public disconnect(): void {
    if (this.disposeTokenReaction) {
      this.disposeTokenReaction();
      this.disposeTokenReaction = null;
    }
    this.socket.disconnect();
    runInAction(() => {
      this.isConnected = false;
    });
  }
}
